import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import {
  RepaymentCalculator,
  StampDutyCalculator,
  BorrowingCalculator,
} from "../components/calculators";
import { GALLERY, CALC_RISK_WARNING } from "../data/content";

const TABS = [
  ["repayment", "Repayments"],
  ["borrowing", "How much can I borrow?"],
  ["stamp-duty", "Stamp duty"],
];

const CalculatorsPage = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return TABS.some(([v]) => v === t) ? t : "repayment";
  });

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TABS.some(([v]) => v === t)) setTab(t);
  }, [searchParams]);

  return (
    <main data-testid="calculators-page" className="bg-brand-bone">
      <Seo
        title="Mortgage Calculators — Repayments, Borrowing & Stamp Duty"
        description="Quick illustrative mortgage calculations: repayments, how much you can borrow, and stamp duty. Not a mortgage offer — speak to BTK for real figures."
        path="/calculators"
        image={GALLERY[6]}
      />
      <PageHero
        testId="calculators-hero"
        eyebrow="Resources · Calculators"
        title="The numbers,"
        italic="in seconds."
        intro="Illustrative figures in seconds — then talk to a human about the real ones."
        image={GALLERY[6]}
      />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <div className="flex flex-wrap gap-3" role="tablist" aria-label="Calculator type">
            {TABS.map(([val, label]) => (
              <button
                key={val}
                role="tab"
                aria-selected={tab === val}
                data-testid={`calc-tab-${val}`}
                onClick={() => setTab(val)}
                className={`px-7 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.22em] transition-[background-color,color,border-color] duration-200 border ${
                  tab === val
                    ? "border-brand bg-brand text-white shadow-[0_14px_32px_rgba(1,86,207,0.3)]"
                    : "border-brand-ink/15 bg-white text-brand-ink/70 hover:border-brand hover:text-brand"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <div className="border border-black/10 bg-white p-8 sm:p-12">
            {tab === "repayment" && <RepaymentCalculator />}
            {tab === "borrowing" && <BorrowingCalculator />}
            {tab === "stamp-duty" && <StampDutyCalculator />}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <aside
            data-testid="calculators-risk-warning"
            className="mt-8 border-l-2 border-brand bg-white p-6 text-[13px] leading-relaxed text-brand-ink sm:p-8"
          >
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-brand">
              Mortgage calculator risk warning
            </p>
            <p className="mt-3">{CALC_RISK_WARNING}</p>
          </aside>
        </Reveal>
      </section>

      <section className="relative bg-brand-ink py-24 overflow-hidden">
        <img aria-hidden src={GALLERY[2]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-ink/60" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow text-brand-gold"><span className="rule-line" />Real figures</p>
            <h2 className="editorial-h mt-6 max-w-2xl text-3xl text-white sm:text-5xl">
              Like the look of <span className="italic text-brand-gold">those numbers?</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70">
              These are illustrations. We'll search 90+ lenders for the real figures — with no obligation.
            </p>
            <Link
              to="/contact"
              data-testid="calc-cta-btn"
              className="group mt-10 inline-flex items-center gap-2 bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
            >
              Get my real figures
              <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default CalculatorsPage;
